/**
 * The money of a material order, in one place. Mirrors backend/core/material-list-math.js (the server stores ITS result; this is what the form shows while
 * typing) and a purchase order's arithmetic: subtotal = sum of quantity x unit price, Tax = a percentage of the subtotal, Other = a fixed amount in dollars;
 * every amount is rounded to cents before it is added.
 */
const cents = (value: number): number => Math.round((Number(value) || 0) * 100);

export interface MaterialOrderTotals {
  subtotal: number;
  tax: number;
  other: number;
  total: number;
}

export function materialOrderTotals(subtotal: number, taxPct: number, otherAmount: number): MaterialOrderTotals {
  const subtotalCents = cents(subtotal);
  const taxCents = Math.round((subtotalCents * (Number(taxPct) || 0)) / 100);
  const otherCents = cents(otherAmount);
  return { subtotal: subtotalCents / 100, tax: taxCents / 100, other: otherCents / 100, total: (subtotalCents + taxCents + otherCents) / 100 };
}

/**
 * The charges a purchase order made from a material order starts with: the SAME two values (a purchase order has a Tax % and an Other amount too), as the text
 * its boxes hold. Nothing is converted, so the purchase order's total equals the material order's to the cent.
 */
export function purchaseOrderChargesOf(taxPct: number, otherAmount: number): { taxPct: string; otherAmount: string } {
  return { taxPct: Number(taxPct) > 0 ? String(Number(taxPct)) : '', otherAmount: Number(otherAmount) > 0 ? String(Number(otherAmount)) : '' };
}

/** A typed percentage or amount as a number: blank is 0, anything that is not a number of 0 or more is null (the form refuses to save it). */
export function parseCharge(text: string | number, max = Infinity): number | null {
  if (typeof text === 'string' && text.trim() === '') return 0;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 && value <= max ? value : null;
}
